import { CheckCircleIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTheme } from '@/app/ThemeContext';
import { TabOption } from '@/components/chariot-ui';
import { NoInformation as NoInformationIcon } from '@/components/chariot-ui';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { Loader } from '@/components/Loader';
import Tabs from '@/components/Tabs';
import { PoeContent as Content } from '@/sections/poe/components/PoeContent';
import { usePOEData } from '@/sections/poe/hooks/usePOEData';
import { usePOESections } from '@/sections/poe/hooks/usePOESections';
import type {
  ExtendedTabOption,
  POEProps,
  SectionContent,
} from '@/sections/poe/types/poe.types';
import { TabDescription } from '@/sections/TabDescription';
import { useGlobalState } from '@/state/global.state';
import { KeyboardListersId } from '@/types';
import { cn } from '@/utils/classname';

const getSectionContent = (
  sections: Record<string, SectionContent>,
  label: string | React.ReactNode,
  copyContents?: Record<string, string>
): string | undefined => {
  if (typeof label !== 'string') return undefined;

  if (copyContents && copyContents[label]) {
    return copyContents[label];
  }

  const content = sections[label];
  if (!content) return undefined;

  if (typeof content === 'string') {
    return content;
  }
  if (Array.isArray(content)) {
    return content.join('\n');
  }
  if (React.isValidElement(content)) {
    return undefined;
  }
  return JSON.stringify(content);
};

const getDisplayContent = (
  sections: Record<string, SectionContent>,
  label: string | React.ReactNode
): SectionContent | undefined => {
  if (typeof label !== 'string') return undefined;
  return sections[label];
};

export function POE(props: POEProps) {
  const {
    poeLocation,
    fallbackPOELocation,
    severity,
    name,
    source,
    attributes = [],
    className,
    riskKey,
    webpage,
  } = props;

  const { keyboardListeners } = useGlobalState();
  const { currentTheme } = useTheme();

  const { file, fileStatus, evidenceFileValue } = usePOEData({
    riskKey,
    poeLocation,
    fallbackPOELocation,
    name,
  });

  const [selectedTabState, setSelectedTab] = useState<ExtendedTabOption | null>(
    null
  );

  const sectionsRef = useRef<HTMLDivElement>(null);

  const handleCopy = useCallback((content: string) => {
    navigator.clipboard.writeText(content).catch(console.error);
  }, []);

  const { sections, availableTabs, copyContents } = usePOESections({
    name,
    source,
    severity,
    file,
    fileStatus,
    evidenceFileValue,
    attributes,
    handleCopy,
    webpage,
    theme: currentTheme,
  });

  const selectedTab = selectedTabState || availableTabs[0];

  const scrollToSection = (sectionId?: string) => {
    if (!sectionId || !sectionsRef.current) return;
    const sectionElement = sectionsRef.current.querySelector(
      `[data-section="${sectionId}"]`
    );
    sectionElement?.scrollIntoView({ behavior: 'smooth' });
  };

  const prevTabsRef = useRef<string>('');
  const tabsKey = availableTabs.map(t => t.value).join(',');

  useEffect(() => {
    if (selectedTab && name) scrollToSection(selectedTab.value);
    if (tabsKey === prevTabsRef.current) return;
    prevTabsRef.current = tabsKey;
    keyboardListeners.add(
      KeyboardListersId.POE,
      {
        ArrowLeft: (event: KeyboardEvent) => {
          event.preventDefault();
          keyboardListeners.remove(KeyboardListersId.POE);
          keyboardListeners.setPOEEnabled(false);
        },
      },
      true
    );
    return () => keyboardListeners.remove(KeyboardListersId.POE);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyboardListeners methods are stable refs; fix tracked in ENG-3498
  }, [tabsKey, selectedTab, name]);

  useEffect(() => {
    return () => {
      keyboardListeners.setPOEEnabled(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only cleanup; fix tracked in ENG-3498
  }, []);

  const handleTabChange = (tab: ExtendedTabOption) => {
    setSelectedTab(tab);
    scrollToSection(tab.value);
    keyboardListeners.setPOEEnabled(true);
  };

  useEffect(() => {
    if (availableTabs.length > 0 && Object.keys(sections).length > 0) {
      const validTab = availableTabs.find(
        tab => tab.value === selectedTab?.value
      );
      if (!validTab) {
        queueMicrotask(() => {
          setSelectedTab(availableTabs[0]);
        });
        keyboardListeners.setPOEEnabled(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyboardListeners methods are stable refs; fix tracked in ENG-3498
  }, [availableTabs, sections, riskKey]);

  if (fileStatus === 'pending') {
    return <Loader isLoading className="m-4 w-[calc(100%-32px)]" />;
  }

  if (!file && availableTabs.length === 0) {
    return (
      <div className="my-4 flex flex-col items-center justify-center">
        <NoInformationIcon className="text-chariot-stroke mb-4" />
        <p className="text-chariot-text-primary text-lg font-bold">
          No Proof of Exploit Available
        </p>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn('flex h-full overflow-hidden', className)}
        data-testid="evidence-tab"
      >
        <div className="flex w-full gap-6">
          <div className="border-chariot-stroke w-48 shrink-0 overflow-y-auto border-r py-4">
            <Tabs
              vertical={true}
              variant="secondary"
              id={KeyboardListersId.POE}
              keyboardNavigation={keyboardListeners.poeEnabled}
              tabs={availableTabs}
              selected={selectedTab || availableTabs[0]}
              align="left"
              onSelect={handleTabChange}
              className="[&>button]:justify-start [&>button]:capitalize"
            />
          </div>

          <div className="flex-1 overflow-y-auto py-4 pr-4" ref={sectionsRef}>
            {selectedTab && (
              <div className="flex flex-col gap-6">
                <div className="border-chariot-stroke pb-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-chariot-text-primary text-xl font-semibold">
                      {selectedTab.heading}
                    </h2>
                  </div>
                  <p className="text-chariot-text-secondary mt-1 text-sm">
                    <TabDescription
                      tabId={selectedTab.value}
                      definitionsFileValue={
                        evidenceFileValue?.startsWith('blob:')
                          ? ''
                          : evidenceFileValue
                      }
                    />
                  </p>
                </div>

                <div className="flex flex-col gap-4">
                  {selectedTab.subTabs?.map((subTab: TabOption) => {
                    const copyContent = getSectionContent(
                      sections,
                      subTab.value,
                      copyContents
                    );
                    const displayContent = getDisplayContent(
                      sections,
                      subTab.value
                    );
                    return displayContent ? (
                      <div
                        key={subTab.value}
                        data-section={subTab.value}
                        className="border-chariot-stroke bg-chariot-background-primary overflow-hidden rounded-md border"
                      >
                        <div className="border-chariot-stroke flex items-center justify-between border-b px-4 py-3">
                          <h3 className="text-chariot-text-primary flex items-center gap-2 font-semibold">
                            {subTab.value === 'matched_at' ? (
                              <CheckCircleIcon className="text-chariot-stroke-info size-6" />
                            ) : null}
                            <span>{subTab.label}</span>
                          </h3>
                          {copyContent && (
                            <CopyToClipboard
                              textToCopy={copyContent}
                              alwaysShowCopyIcon
                              iconClassName="size-4 text-chariot-text-secondary"
                            />
                          )}
                        </div>
                        <div className="p-4">
                          <Content content={displayContent} />
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
